import type { Survey } from 'types';
import type { SurveyDraft } from './useSurvey';
import { useState } from 'react';
import { useForm } from 'hooks/useForm';
import { SURVEY_TYPES } from 'definitions';
import { questionsProblem } from './survey-questions';
import { targetingProblem } from './survey-targeting';

export const BUILDER_STEPS = ['Template', 'Questions', 'Targeting', 'Trigger', 'Theme'] as const;
export type BuilderStep = (typeof BUILDER_STEPS)[number];

type Template = { type: Survey['type']; name: string; blurb: string; questions: SurveyDraft['questions'] };

/**
 * One template per survey type. The wording is the part customers get wrong — an
 * NPS question that does not ask about recommending is not NPS, whatever scale it
 * renders — so the defaults are the standard phrasings rather than empty fields.
 *
 * Custom is the blank-ish one: a starting mix of a rating, a multiple choice and a
 * comment, every part of it editable, with no instrument to stay faithful to.
 */
export const TEMPLATES: Record<Survey['type'], Template> = {
  NPS: {
    type: 'NPS',
    name: 'Net Promoter Score',
    blurb: 'Loyalty, on a 0–10 scale. Best asked after a completed purchase or a support interaction.',
    questions: [
      { kind: 'score', label: 'How likely are you to recommend us to a friend or colleague?', required: true },
      { kind: 'open text', label: 'What is the main reason for your score?', required: false },
    ],
  },
  CSAT: {
    type: 'CSAT',
    name: 'Customer Satisfaction',
    blurb: 'Satisfaction, on a 1–5 star scale. Best asked immediately after a specific interaction.',
    questions: [
      { kind: 'score', label: 'How satisfied are you with your experience?', required: true },
      { kind: 'open text', label: 'What would have made this better?', required: false },
    ],
  },
  CES: {
    type: 'CES',
    name: 'Customer Effort Score',
    blurb: 'Effort, on a 1–7 scale. Best asked after checkout or a self-service task.',
    questions: [
      { kind: 'score', label: 'How easy was it to get what you needed today?', required: true },
      { kind: 'open text', label: 'What made it easy or difficult?', required: false },
    ],
  },
  Custom: {
    type: 'Custom',
    name: 'Custom survey',
    blurb: 'Your own questions: ratings, single or multiple choice, and text, up to five, any of them optional.',
    questions: [
      { kind: 'score', label: 'How would you rate your visit today?', required: true, config: { scale: 'CSAT' } },
      {
        kind: 'multiple choice',
        label: 'What did you come here to do?',
        required: false,
        config: { options: ['Browse', 'Buy something', 'Compare options', 'Get support'] },
      },
      { kind: 'open text', label: 'Anything we could do better?', required: false },
    ],
  },
};

/*
 * The three functions below are pure and exported deliberately.
 *
 * CONVENTIONS puts computation outside the component, and there is a testing
 * reason too: useForm.update() writes back to the DOM through formRef, so
 * anything reachable only through the hook cannot be exercised without mounting
 * the whole wizard. The validation rules are the part worth testing, so they live
 * where a test can reach them.
 */

export const draftFor = (type: Survey['type']): SurveyDraft => {
  const template = TEMPLATES[type];
  return {
    name: template.name,
    type,
    position: 'bottom right',
    triggerConfig: { type: 'delay', seconds: 10 },
    theme: { accentColor: '#2563eb', borderRadius: 12 },
    frequencyCap: { cooldownDays: 30, maxPrompts: 2 },
    texts: {},
    targeting: [],
    experimentId: null,
    variantIds: [],
    sampleRate: 100,
    questions: template.questions.map((question) => ({ ...question })),
  };
};

/**
 * useForm types its state as Partial<T>, because the DOM is the schema and a
 * field that has not rendered has no value. The wizard always seeds a complete
 * draft, so the partiality is a type artifact rather than a real possibility.
 * Normalising once keeps four step components free of `?? default` noise, and the
 * fallbacks double as a repair if a field genuinely goes missing.
 */
export const normaliseDraft = (partial: Partial<SurveyDraft>): SurveyDraft => ({
  name: partial.name ?? '',
  type: partial.type ?? 'NPS',
  position: partial.position ?? 'bottom right',
  triggerConfig: partial.triggerConfig ?? { type: 'page view' },
  theme: partial.theme ?? {},
  frequencyCap: partial.frequencyCap ?? { cooldownDays: 30, maxPrompts: 2 },
  texts: partial.texts ?? {},
  targeting: partial.targeting ?? [],
  experimentId: partial.experimentId ?? null,
  variantIds: partial.variantIds ?? [],
  sampleRate: partial.sampleRate ?? 100,
  questions: partial.questions ?? [],
});

export const stepValidityFor = (draft: SurveyDraft): Record<BuilderStep, boolean> => ({
  Template: SURVEY_TYPES.includes(draft.type) && draft.name.trim().length > 0,
  Questions: questionsProblem(draft) === null,
  Targeting: targetingProblem(draft) === null,
  Trigger: (() => {
    const trigger = draft.triggerConfig;
    if (trigger.type === 'delay') return trigger.seconds >= 0 && trigger.seconds <= 600;
    if (trigger.type === 'scroll depth') return trigger.percent >= 1 && trigger.percent <= 100;
    if (trigger.type === 'event') return trigger.eventName.trim().length > 0;
    return true;
  })(),
  Theme: themeProblem(draft) === null,
});

const isWhole = (value: number, min: number, max: number) => Number.isInteger(value) && value >= min && value <= max;

/**
 * What stops the Theme step, or null. Mirrors the API's Zod limits (schemas.ts), so
 * a value the server would refuse with a 400 at save is caught here instead.
 */
export function themeProblem(draft: SurveyDraft): string | null {
  // Interpolated into a stylesheet on the customer's page, so only a hex literal.
  if (!/^#[0-9a-fA-F]{6}$/.test(draft.theme.accentColor ?? '#2563eb')) {
    return 'The accent colour has to be a six-digit hex value, like #2563eb';
  }
  if (!isWhole(draft.theme.borderRadius ?? 12, 0, 32)) return 'The corner radius is a whole number from 0 to 32';
  if (!isWhole(draft.frequencyCap.cooldownDays, 0, 365))
    return 'The days before re-asking are a whole number from 0 to 365';
  if (!isWhole(draft.frequencyCap.maxPrompts, 1, 10)) return 'The prompts per visitor are a whole number from 1 to 10';
  return null;
}

/**
 * A saved survey's questions in the draft's shape. The one mapping both for
 * seeding the builder and for telling whether the questions were edited: if the
 * two differed, every save would look like a question change, and a running
 * survey (whose questions cannot be replaced) would refuse every edit with a 409.
 *
 * config travels with the question: dropping it would wipe options, labels and a
 * Custom question's scale on the next save.
 */
export const questionsOf = (survey: Survey): SurveyDraft['questions'] =>
  survey.questions.map((question) => ({
    kind: question.kind,
    label: question.label,
    required: question.required,
    config: question.config,
  }));

const draftFromSurvey = (survey: Survey): SurveyDraft => ({
  name: survey.name,
  type: survey.type,
  position: survey.position,
  triggerConfig: survey.triggerConfig,
  theme: survey.theme,
  frequencyCap: survey.frequencyCap,
  texts: survey.texts ?? {},
  targeting: survey.targeting.map(({ url, condition, device }) => ({ url, condition, device })),
  experimentId: survey.experimentId,
  variantIds: survey.variantIds,
  sampleRate: survey.sampleRate,
  questions: questionsOf(survey),
});

export const useSurveyBuilder = (existing?: Survey) => {
  const [step, setStep] = useState<BuilderStep>('Template');

  /**
   * ONE useForm for the whole wizard.
   *
   * useForm resolves [name=…] against the document, not the form element, so two
   * mounted forms sharing a field name write into the wrong input — silently
   * (docs/frontend/shared-foundations.md). A wizard that renders a fresh <form>
   * per step is exactly that shape. Every step is therefore rendered inside a
   * single form and hidden with CSS, which also means state survives step
   * navigation with no draft-merging code.
   *
   * Names are flat rather than nested in <fieldset name="step">, so the state
   * shape matches the DTO the API expects.
   */
  const form = useForm<SurveyDraft>(existing ? draftFromSurvey(existing) : draftFor('NPS'));

  const draft = normaliseDraft(form.state);

  /** Complete-state functional update, so callers can narrow the trigger union. */
  const updateDraft = (fn: (current: SurveyDraft) => SurveyDraft) =>
    form.update((partial) => fn(normaliseDraft(partial)));

  /**
   * The questions are fixed once a survey has run: the API replaces them on a
   * Draft only, because responses point at them. So is the type.
   */
  const questionsLocked = !!existing && existing.status !== 'Draft';

  const selectTemplate = (type: Survey['type']) => {
    // Picking the type already chosen changes nothing; anything else would throw
    // away edited questions for no reason.
    if (type === draft.type || questionsLocked) return;
    // A new survey starts over from the template, name included. An existing one
    // keeps its name, targeting, texts and theme and takes only the template's type
    // and questions, so switching instrument cannot leave an NPS question on a CES
    // survey, nor quietly reset who the survey shows to.
    const fresh = draftFor(type);
    updateDraft((state) => (existing ? { ...state, type, questions: fresh.questions } : fresh));
  };

  const stepValidity = stepValidityFor(draft);
  const index = BUILDER_STEPS.indexOf(step);

  return {
    form,
    draft,
    updateDraft,
    step,
    setStep,
    steps: BUILDER_STEPS,
    stepIndex: index,
    stepValidity,
    /** What stops the Questions step, named, so a dead Continue can say why. */
    questionsProblem: questionsProblem(draft),
    themeProblem: themeProblem(draft),
    targetingProblem: targetingProblem(draft),
    canAdvance: stepValidity[step],
    canSubmit: BUILDER_STEPS.every((name) => stepValidity[name]),
    isFirst: index === 0,
    isLast: index === BUILDER_STEPS.length - 1,
    next: () => {
      const following = BUILDER_STEPS[index + 1];
      if (following && stepValidity[step]) setStep(following);
    },
    back: () => {
      const previous = BUILDER_STEPS[index - 1];
      if (previous) setStep(previous);
    },
    selectTemplate,
    questionsLocked,
    /** The survey's own website; undefined for a new survey, which uses the selected one. */
    websiteId: existing?.websiteId ?? undefined,
  };
};

export type SurveyBuilderManager = ReturnType<typeof useSurveyBuilder>;
